import React, {memo} from 'react';

import cs from 'classnames';
import {Location} from 'history';
import {colors} from 'js-libs/sweetcommons/styles';
import pickBy from 'lodash/pickBy';
import {InjectedRouter} from 'react-router';
import styled from 'styled-components';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {ReplaceableLink} from 'app/components/general/FullHTMLLink';
import {gt} from 'app/utils/i18n';
import {serialize} from 'app/utils/request';
import {getPathFromURL} from 'app/utils/urls';
import {AbstractVersionType} from 'app/version/types';

import {LocationQuery} from './location-query';
import {VersionItem} from './VersionItem';

interface Props<V extends AbstractVersionType> {
  canDelete: boolean;
  versionGroup: Array<V>;
  router: InjectedRouter;
  location: Location<LocationQuery>;
  selectedVersion: number | null | string;
  deleteVersion: (version: V) => Promise<any>;
}

export const GroupedVersionsItem = memo<Props<AbstractVersionType>>(
  <V extends AbstractVersionType>({
    canDelete,
    versionGroup,
    router,
    location,
    selectedVersion,
    deleteVersion,
  }: Props<V>) => {
    const latestVersion = versionGroup[0];

    if (versionGroup.length <= 1) {
      return null;
    }

    const {isExpanded, toggleExpandedLink} = useExpandedVersionsParams(
      latestVersion.id.toString(),
      latestVersion.html_url,
      location,
      selectedVersion != null &&
        versionGroup.map((v) => v.id).indexOf(selectedVersion) > -1
    );
    const groupIsActive =
      selectedVersion === latestVersion.id &&
      location.query['selected-version-group'] === latestVersion.id.toString();
    React.useEffect(() => {
      // on mount, auto-expand the group containing the selected version
      const expanded = (location.query['expanded-versions'] || '')
        .split(',')
        .filter((v) => v);
      const isExpanded = expanded.indexOf(versionGroup[0].id.toString()) > -1;

      for (let i = 0; i < versionGroup.length; i++) {
        if (versionGroup[i].id === selectedVersion && !isExpanded) {
          router.replace({
            pathname: location.pathname,
            query: {
              ...location.query,
              'expanded-versions': expanded
                .concat([versionGroup[0].id.toString()])
                .join(','),
            },
          });
        }
      }
    }, []);
    return (
      <React.Fragment>
        <StyledContainerLi active={groupIsActive}>
          <ReplaceableLink
            className="p-2 d-flex align-items-center"
            to={toggleExpandedLink()}
            replace
          >
            <StyledColoredDiv active={groupIsActive}>
              <i
                className={cs(
                  'fa fa-fw',
                  isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'
                )}
              />
            </StyledColoredDiv>
          </ReplaceableLink>
          <ReplaceableLink
            replace
            className="small py-2 pl-0 pr-2 flex-grow-1"
            to={{
              pathname: getPathFromURL(latestVersion.html_url),
              query: {
                ...location.query,
                'selected-version-group': latestVersion.id,
              },
            }}
          >
            <StyledColoredDiv active={groupIsActive}>
              <div>{gt('%d versions by', versionGroup.length)}</div>
              <div className="font-weight-bold">
                {latestVersion.editor != null
                  ? latestVersion.editor.name
                  : gt('Deleted user')}
              </div>
              <ElapsedTime
                className="align-items-center"
                tooltipPlacement="top"
                timestamp={latestVersion.created_at}
                wrapTooltip={(text) => <small>{gt('Created %s', text)}</small>}
              />
            </StyledColoredDiv>
          </ReplaceableLink>
        </StyledContainerLi>
        {isExpanded
          ? versionGroup.map((draft) => (
              <VersionItem
                canDelete={canDelete}
                compactDraftLayout={true}
                key={draft.id}
                searchParams={removeSelectedVersionGroupParam(location.query)}
                onDelete={() => deleteVersion(draft)}
                version={draft}
                isSelected={selectedVersion === draft.id && !groupIsActive}
              />
            ))
          : null}
      </React.Fragment>
    );
  }
);

interface StyledActiveProp {
  active?: boolean;
}

const StyledColoredDiv = styled.div.attrs<StyledActiveProp>(({active}) => ({
  className: cs({
    'text-white': active,
    'text-secondary': !active,
  }),
}))<StyledActiveProp>``;

const StyledContainerLi = styled.li.attrs<StyledActiveProp>(({active}) => ({
  active,
  className: cs('d-flex list-group-item p-0', {
    active: active,
  }),
}))<StyledActiveProp>`
  background-color: ${({active}) => (!active ? colors.lightBg : 'transparent')};
`;

function useExpandedVersionsParams(
  versionId: string,
  versionHTMLURL: string,
  location: Location<LocationQuery>,
  draftIsSelected: boolean
) {
  const expandedVersions = (location.query['expanded-versions'] || '')
    .split(',')
    .filter((v) => v);
  const isExpanded = expandedVersions.indexOf(versionId) > -1;

  function toggleExpandedLink() {
    const updated = isExpanded
      ? expandedVersions.filter((v) => v !== versionId)
      : expandedVersions.concat([versionId]);
    return {
      pathname:
        isExpanded && draftIsSelected
          ? getPathFromURL(versionHTMLURL)
          : location.pathname,
      query: pickBy(
        {
          ...location.query,
          'expanded-versions': updated.join(','),
          'selected-version-group': isExpanded
            ? versionId
            : location.query['selected-version-group'],
        },
        (value) => value !== ''
      ),
    };
  }

  return {isExpanded, toggleExpandedLink};
}

export function removeSelectedVersionGroupParam(
  params: Record<string, string>
): string {
  return `?${serialize(
    pickBy({...params, 'selected-version-group': ''}, (v) => v !== '')
  )}`;
}
